import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Calendar, TrendingUp, TrendingDown } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface TimelineEvent {
  date: string;
  title: string;
  description: string;
  trend?: "improving" | "worsening" | "stable";
  category: string;
}

interface ClinicalTimelineProps {
  events: TimelineEvent[];
}

const trendConfig = {
  improving: { icon: TrendingUp, color: "text-emerald-600", label: "Melhora" },
  worsening: { icon: TrendingDown, color: "text-red-600", label: "Piora" },
  stable: { icon: TrendingUp, color: "text-gray-500", label: "Estável" }
};

export function ClinicalTimeline({ events }: ClinicalTimelineProps) {
  return (
    <Card className="shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="h-5 w-5 text-primary" />
          Linha do Tempo Clínica
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative space-y-6">
          {/* Linha vertical */}
          <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-border"></div>
          
          {events.map((event, index) => {
            const trendInfo = event.trend ? trendConfig[event.trend] : null;
            const TrendIcon = trendInfo?.icon;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="relative pl-12"
              >
                {/* Ponto na linha */}
                <div className="absolute left-2 top-1 w-4 h-4 rounded-full bg-primary border-4 border-background"></div>
                
                <div className="bg-card border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">
                        {format(new Date(event.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </p>
                      <h4 className="font-semibold text-sm mb-1">{event.title}</h4>
                    </div>
                    {trendInfo && TrendIcon && (
                      <div className={`flex items-center gap-1 ${trendInfo.color}`}>
                        <TrendIcon className="h-4 w-4" />
                        <span className="text-xs font-medium">{trendInfo.label}</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-2">{event.description}</p>
                  
                  <Badge variant="secondary" className="text-xs">
                    {event.category}
                  </Badge>
                </div>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
